import { Component, Input, OnChanges, OnInit } from "@angular/core";
import { getApp } from "../app";
import { CustomerName } from "../../../../shared/config/customers";
import { ModalComponent } from "../modal/modal.component";
import { StringUtils } from "../../../../shared/utils/string.utils";
import { Type } from "../../../../shared/components";

const SYSTEM_ENGINEER = "systemEngineer";

/** A system the current case or post may be transferred to. */
export class TransferTarget {
  customer: CustomerName = "" as CustomerName;
  title: string = "";
}

@Component({
  selector: "app-transfer",
  templateUrl: "./transfer.component.html",
  styleUrls: ["./transfer.component.scss"],
  standalone: false
})
export class TransferComponent implements OnInit, OnChanges {
  app = getApp((app) => {
    this.app = app;
  });

  @Input() type: Type = "thread";
  @Input() id = "";
  @Input() post: any = null;
  @Input() hasText = true;

  /** system chosen in the button that opened the confirm modal */
  targetCustomer: CustomerName | null = null;
  /** systems this button offers, with the tooltip already built */
  targets: TransferTarget[] = [];
  /** should this button be rendered at all */
  showButton = false;
  /** label of the button */
  buttonText = "";
  /** message of the confirm modal */
  question = "";

  constructor() {}

  ngOnInit() {
    this.update();
  }

  /** The bound id changes when the user steps to another case or post. */
  ngOnChanges() {
    this.update();
  }

  /** Work out everything the template needs. */
  update() {
    this.targets = this.buildTargets();
    this.buttonText = this.buildButtonText();
    this.question = this.buildQuestion();
    this.showButton = this.buildVisibility();
  }

  buildTargets(): TransferTarget[] {
    const customers = this.app.additionalInfo.transfer.transferTargets;
    if (customers == null) {
      return [];
    }
    const handled = this.getHandledCustomers();
    return (customers as CustomerName[])
      .filter((customer) => !handled.includes(customer))
      .map((customer) => ({
        customer,
        title:
          this.app.text.transfer.transferTo +
          ": " +
          this.app.customers.getText(customer)
      }));
  }

  getHandledCustomers(): string[] {
    const source = this.type === "post" ? this.post : this.app.thread.thread;
    if (source == null) {
      return [];
    }
    const from =
      this.type === "post"
        ? source.transferredFrom
        : source[this.app.fieldId.thread.transferredFrom];
    const to =
      this.type === "post"
        ? source.transferredTo
        : source[this.app.fieldId.thread.transferredTo];

    return [from, to].filter(
      (customer) => !StringUtils.isNullOrEmpty(customer)
    );
  }

  buildVisibility(): boolean {
    if (this.targets.length === 0) {
      return false;
    }
    if (!this.hasRolePermission()) {
      return false;
    }
    if (this.app.state.jobTitle !== SYSTEM_ENGINEER) {
      return false;
    }
    if (this.type === "post") {
      return this.app.transfer.isCaseTransferred();
    }
    return true;
  }

  hasRolePermission(): boolean {
    if (this.type === "post") {
      return this.app.permission.transfer.transferPost;
    }
    return this.app.permission.transfer.transferCase;
  }

  buildButtonText(): string {
    if (this.type === "post") {
      return this.app.text.transfer.transferPost;
    }
    return this.app.text.transfer.transferCase;
  }

  buildQuestion(): string {
    if (this.type === "post") {
      return this.app.text.transfer.transferPostQuestion;
    }
    return this.app.text.transfer.transferCaseQuestion;
  }

  openConfirm(target: CustomerName, modal: ModalComponent) {
    this.targetCustomer = target;
    modal.open();
  }

  async transfer() {
    if (this.targetCustomer == null) {
      return;
    }
    if (this.type === "post") {
      await this.app.transfer.transferPost(this.post, this.targetCustomer);
    } else {
      await this.app.transfer.transferCase(this.targetCustomer);
    }
    this.update();
  }
}
